import {
  Box,
  Flex,
  Grid,
  HStack,
  VStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
  useDisclosure,
  useToast,
  Collapse,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaFolder, FaFolderOpen, FaFolderPlus, FaEllipsisV, FaPlus, FaTrash, FaEdit, FaChevronDown, FaChevronRight, FaUser } from 'react-icons/fa';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { MyAgentCard } from './MyAgentCard';
import { CreateCollectionModal } from './CreateCollectionModal';
import { EditCollectionModal } from './EditCollectionModal';
import { AddPersonasToCollectionModal } from './AddPersonasToCollectionModal';
import { CreateNewProjectModal } from '../Projects/CreateNewProjectModal';
import {
  useGetMyAgentCollectionsQuery,
  useGetAgentCollectionQuery,
  useDeleteAgentCollectionMutation,
  useRemoveDigitalTwinFromCollectionMutation,
  CustomAgent,
  DigitalTwin,
} from '../../generated/graphql';

interface CollectionsViewProps {
  searchTerm?: string;
}

export const CollectionsView = ({ searchTerm = '' }: CollectionsViewProps) => {
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [editingCollection, setEditingCollection] = useState<{ id: string; name: string; description?: string | null } | null>(null);
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const toast = useToast();

  const { data, loading, refetch } = useGetMyAgentCollectionsQuery();
  const [deleteCollection] = useDeleteAgentCollectionMutation();

  const collections = data?.getMyAgentCollections || [];

  // Filter collections by search term
  const filteredCollections = searchTerm.trim()
    ? collections.filter(
        (c) =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : collections;

  const toggleCollection = (collectionId: string) => {
    setExpandedCollections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });
  };

  const handleDelete = async (collectionId: string, collectionName: string) => {
    try {
      await deleteCollection({
        variables: { id: collectionId },
      });

      toast({
        title: 'Collection deleted',
        description: `"${collectionName}" has been deleted.`,
        status: 'success',
        duration: 3000,
      });

      refetch();
    } catch (error: any) {
      toast({
        title: 'Error deleting collection',
        description: error.message || 'Something went wrong.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleEdit = (collection: { id: string; name: string; description?: string | null }) => {
    setEditingCollection(collection);
    editModal.onOpen();
  };

  const handleCollectionCreated = () => {
    refetch();
  };

  const handleCollectionUpdated = () => {
    refetch();
    setEditingCollection(null);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
          Loading collections...
        </HelivanText>
      </Box>
    );
  }

  return (
    <VStack spacing={4} width="full" maxWidth="1400px" align="stretch">
      {/* Header with create button */}
      <HStack justifyContent="space-between">
        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
          {filteredCollections.length} collection{filteredCollections.length !== 1 ? 's' : ''}
        </HelivanText>
        <HelivanButton variant="outline" size="sm" onClick={createModal.onOpen}>
          <HStack spacing={1}>
            <FaPlus size={12} />
            <span>New Collection</span>
          </HStack>
        </HelivanButton>
      </HStack>

      {filteredCollections.length === 0 ? (
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          py={16}
          gap={2}
        >
          <Flex
            as="button"
            w="56px"
            h="56px"
            borderRadius="16px"
            bg="#00477D"
            align="center"
            justify="center"
            mb={2}
            cursor="pointer"
            transition="all 0.15s"
            boxShadow="0 2px 8px rgba(0,71,125,0.3)"
            _hover={{
              bg: '#003d6b',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
            }}
            _active={{ transform: 'scale(0.96)' }}
            onClick={createModal.onOpen}
          >
            <FaFolderPlus size={26} color="white" />
          </Flex>
          <HelivanText variant="largeBody" style={{ fontWeight: 600, color: '#1a1a2e' }}>
            No collections yet
          </HelivanText>
          <HelivanText variant="smallBody" colorOverride="#64748b">
            Group your agents and personas into collections for easy project creation
          </HelivanText>
        </Flex>
      ) : (
        <VStack spacing={3} align="stretch">
          {filteredCollections.map((collection) => (
            <CollectionItem
              key={collection.id}
              collection={collection}
              isExpanded={expandedCollections.has(collection.id)}
              onToggle={() => toggleCollection(collection.id)}
              onEdit={() => handleEdit(collection)}
              onDelete={() => handleDelete(collection.id, collection.name)}
              onRefetch={refetch}
            />
          ))}
        </VStack>
      )}

      <CreateCollectionModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSuccess={handleCollectionCreated}
      />

      {editingCollection && (
        <EditCollectionModal
          isOpen={editModal.isOpen}
          onClose={() => {
            editModal.onClose();
            setEditingCollection(null);
          }}
          onSuccess={handleCollectionUpdated}
          collection={editingCollection}
        />
      )}
    </VStack>
  );
};

interface CollectionItemProps {
  collection: {
    id: string;
    name: string;
    description?: string | null;
    agentCount: number;
    digitalTwinCount: number;
  };
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRefetch: () => void;
}

const CollectionItem = ({
  collection,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onRefetch,
}: CollectionItemProps) => {
  const createProjectModal = useDisclosure();
  const addPersonasModal = useDisclosure();
  const { data, loading } = useGetAgentCollectionQuery({
    variables: { id: collection.id },
    skip: !isExpanded,
  });

  const agents = (data?.getAgentCollection?.agents || []) as CustomAgent[];
  const digitalTwins = (data?.getAgentCollection?.digitalTwins || []) as Pick<DigitalTwin, 'id' | 'name' | 'type'>[];
  const totalItems = agents.length + digitalTwins.length;
  const existingTwinIds = new Set(digitalTwins.map((t) => t.id));

  // Open the "add personas" picker; expand first so the collection's current
  // personas are loaded (used to mark which are already in).
  const openAddPersonas = () => {
    if (!isExpanded) onToggle();
    addPersonasModal.onOpen();
  };

  return (
    <>
      <CreateNewProjectModal
        isOpen={createProjectModal.isOpen}
        onClose={createProjectModal.onClose}
        hasTokens={true}
        preSelectedCollectionId={collection.id}
        preSelectedCollectionName={collection.name}
      />
      <AddPersonasToCollectionModal
        isOpen={addPersonasModal.isOpen}
        onClose={addPersonasModal.onClose}
        collectionId={collection.id}
        collectionName={collection.name}
        existingTwinIds={existingTwinIds}
        onSuccess={onRefetch}
      />
      <Box
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="lg"
      bg="white"
      overflow="hidden"
    >
      {/* Collection header */}
      <HStack
        p={4}
        justifyContent="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: HelivanColor.background.secondary }}
      >
        <HStack spacing={3}>
          {isExpanded ? (
            <FaChevronDown size={12} color={HelivanColor.text.blueMid} />
          ) : (
            <FaChevronRight size={12} color={HelivanColor.text.blueMid} />
          )}
          {isExpanded ? (
            <FaFolderOpen size={20} color={HelivanColor.text.blueMid} />
          ) : (
            <FaFolder size={20} color={HelivanColor.text.blueMid} />
          )}
          <VStack align="start" spacing={0}>
            <HelivanText variant="smallBody">{collection.name}</HelivanText>
            {collection.description && (
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                {collection.description}
              </HelivanText>
            )}
          </VStack>
        </HStack>

        <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
            {collection.agentCount + collection.digitalTwinCount} item{(collection.agentCount + collection.digitalTwinCount) !== 1 ? 's' : ''}
          </HelivanText>
          <Menu isLazy strategy="fixed" placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<FaEllipsisV size={12} />}
              variant="ghost"
              size="sm"
              aria-label="Collection options"
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
            />
            <Portal>
              <MenuList
                fontSize="13px"
                zIndex={1400}
                boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                py={2}
                minW="180px"
              >
                <MenuItem
                  icon={<FaPlus size={12} />}
                  onClick={() => createProjectModal.onOpen()}
                  py={2}
                  px={3}
                  _hover={{ bg: HelivanColor.background.secondary }}
                >
                  Create Project
                </MenuItem>
                <MenuItem
                  icon={<FaUser size={12} />}
                  onClick={openAddPersonas}
                  py={2}
                  px={3}
                  _hover={{ bg: HelivanColor.background.secondary }}
                >
                  Add personas
                </MenuItem>
                <MenuDivider my={1} borderColor={HelivanColor.border.primary} />
                <MenuItem
                  icon={<FaEdit size={12} />}
                  onClick={onEdit}
                  py={2}
                  px={3}
                  _hover={{ bg: HelivanColor.background.secondary }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={<FaTrash size={12} />}
                  color="red.500"
                  onClick={onDelete}
                  py={2}
                  px={3}
                  _hover={{ bg: 'red.50' }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
      </HStack>

      {/* Expanded content */}
      <Collapse in={isExpanded}>
        <Box p={4} pt={0} borderTop="1px solid" borderTopColor={HelivanColor.border.primary}>
          <HStack justifyContent="flex-end" pt={3} pb={1}>
            <HelivanButton variant="outline" size="sm" onClick={openAddPersonas}>
              <HStack spacing={1}>
                <FaUser size={11} />
                <span>Add personas</span>
              </HStack>
            </HelivanButton>
          </HStack>
          {loading ? (
            <Box textAlign="center" py={4}>
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                Loading items...
              </HelivanText>
            </Box>
          ) : totalItems === 0 ? (
            <Box textAlign="center" py={4}>
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                No items in this collection yet
              </HelivanText>
            </Box>
          ) : (
            <Grid
              templateColumns={[
                'repeat(2, 1fr)',
                'repeat(2, 1fr)',
                'repeat(3, 1fr)',
                'repeat(4, 1fr)',
              ]}
              gap={4}
              mt={2}
            >
              {agents.map((agent) => (
                <MyAgentCard
                  key={agent.id}
                  agent={agent}
                  onUpdate={onRefetch}
                  collectionId={collection.id}
                />
              ))}
              {digitalTwins.map((twin) => (
                <DigitalTwinCard
                  key={twin.id}
                  twin={twin}
                  onUpdate={onRefetch}
                  collectionId={collection.id}
                />
              ))}
            </Grid>
          )}
        </Box>
      </Collapse>
    </Box>
    </>
  );
};

// Simple card component for digital twins displayed in collections
interface DigitalTwinCardProps {
  twin: Pick<DigitalTwin, 'id' | 'name' | 'type'>;
  onUpdate: () => void;
  collectionId: string;
}

const DigitalTwinCard = ({ twin, onUpdate, collectionId }: DigitalTwinCardProps) => {
  const toast = useToast();
  const [removeFromCollection] = useRemoveDigitalTwinFromCollectionMutation();

  const handleRemoveFromCollection = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await removeFromCollection({
        variables: {
          collectionId,
          digitalTwinId: twin.id,
        },
      });
      onUpdate();
      toast({
        title: 'Removed from collection',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove from collection',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Flex
      width="full"
      minWidth="240px"
      maxWidth="300px"
      flexDirection="column"
      bg="white"
      borderRadius="12px"
      overflow="hidden"
      boxShadow="0 1px 3px rgba(0,0,0,0.06)"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      _hover={{
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,71,125,0.1)',
        borderColor: HelivanColor.text.helivanBlue,
      }}
      transition="all 0.15s ease"
    >
      {/* Header with type indicator */}
      <Flex
        px="14px"
        py="10px"
        bg={HelivanColor.background.secondary}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor={HelivanColor.border.primary}
      >
        <HStack spacing={2}>
          <FaUser size={12} color={HelivanColor.text.helivanBlue} />
          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
            Persona
          </HelivanText>
        </HStack>
        {twin.type && (
          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
            {twin.type}
          </HelivanText>
        )}
      </Flex>

      <Flex flexDirection="column" p="14px" gap="10px">
        <Flex width="full" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <HStack spacing={3} flex="1" alignItems="center">
            <Box
              width="32px"
              height="32px"
              borderRadius="full"
              bg={HelivanColor.text.helivanBlue}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FaUser size={14} color="white" />
            </Box>
            <HelivanText variant="smallBody" style={{ fontWeight: 600 }}>
              {twin.name}
            </HelivanText>
          </HStack>

          <Menu isLazy strategy="fixed" placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<FaEllipsisV size={12} />}
              size="xs"
              variant="ghost"
              minW="28px"
              h="28px"
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
            <Portal>
              <MenuList
                fontSize="13px"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                zIndex={1400}
                boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                py={2}
                minW="180px"
              >
                <MenuItem
                  icon={<FaTrash size={12} />}
                  color="red.500"
                  onClick={handleRemoveFromCollection}
                  py={2}
                  px={3}
                  _hover={{ bg: 'red.50' }}
                >
                  Remove from Collection
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      </Flex>
    </Flex>
  );
};
